import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import Popup, { ToolbarItem as PopupToolbarItem } from "devextreme-react/popup";
import { LoadPanel } from "devextreme-react/load-panel";
import { SelectBox, TextBox } from "devextreme-react";
import notify from "devextreme/ui/notify";
import DataSource from "devextreme/data/data_source";
import { createStore } from "devextreme-aspnet-data-nojquery";
import data from "../../assets/apiRoutes.json";
import { MESSAGES } from "../../common/Messages";

// Create a customer portal account (T20260914.0001, functional requirements 2-6).
//
// No password field by design: the CSR Manager never sets one. The account is created without a
// password and in "Invited" status, so it cannot be logged in to until the customer sets their own -
// which is what keeps Freezpak from ever holding a customer's password.

// FR4's rule: a local part, exactly one "@", and a domain carrying at least one dot. The server applies
// the same check, so a direct API call cannot get past it either.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

const CreateCustomerAccount = ({ visible, onClose, onCreated, oidc }) => {
    const accessToken = oidc.user.access_token;

    const [customerRowId, setCustomerRowId] = useState(null);
    const [customerId, setCustomerId] = useState("");
    // Which facility the chosen customer lives in. The account is given that facility, not whichever
    // one the CSR Manager happens to be working in, or it would be scoped to a customer in a database
    // it cannot open.
    const [customerCompanyId, setCustomerCompanyId] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [saving, setSaving] = useState(false);

    // The account's facility is not asked for. The BR's create form is Customer ID, First Name, Last
    // Name and Email, so the server gives the account the facility the CSR Manager is working in.

    // Only customers that exist and are active are offered, which is how FR3 is kept: an account can
    // never be pointed at a customer the operator could not have chosen.
    //
    // allFacilities searches every facility the operator may reach rather than only the one they are in,
    // and returns the identity CODE, the name and the facility as separate fields - the code on its own
    // is what the account's identityId claim has to carry.
    const customers = useMemo(
        () =>
            new DataSource({
                store: createStore({
                    key: "id",
                    loadUrl: `${window._env_.REACT_APP_APIS}/${data.Common.GetLookup}`,
                    loadParams: {
                        module: "id",
                        lookupType: "Customer",
                        showLookupGrid: true,
                        activeOnly: true,
                        allFacilities: true,
                    },
                    onBeforeSend: (method, ajaxOptions) => {
                        ajaxOptions.headers = { Authorization: `Bearer ${accessToken}` };
                    },
                }),
                sort: [{ selector: "identityid", desc: false }],
                paginate: true,
                pageSize: 100,
            }),
        [accessToken]
    );

    // The facility is part of the label, not decoration: the same customer code can exist in more than
    // one facility, and without it two entries would look identical.
    const customerDisplay = (item) => {
        if (!item) return "";
        const code = (item.identityid || "").trim();
        const name = (item.customerName || "").trim();
        const facility = (item.facilityName || "").trim();
        const label = name ? `${code} - ${name}` : code;
        return facility ? `${label}  (${facility})` : label;
    };

    const reset = () => {
        setCustomerRowId(null);
        setCustomerId("");
        setCustomerCompanyId("");
        setFirstName("");
        setLastName("");
        setEmail("");
        setSaving(false);
    };

    const handleClose = () => {
        reset();
        if (onClose) onClose();
    };

    const handleSave = () => {
        const trimmedEmail = (email || "").trim();

        if (!customerRowId) {
            notify(MESSAGES.CUSTOMER_ACCOUNT_CUSTOMER_REQUIRED, "error", 4000);
            return;
        }
        if (!EMAIL_PATTERN.test(trimmedEmail)) {
            notify(MESSAGES.CUSTOMER_ACCOUNT_EMAIL_INVALID, "error", 4000);
            return;
        }

        setSaving(true);
        axios
            .post(
                `${window._env_.REACT_APP_AUTH_URL}/${data.idp.CreateCustomerAccount}`,
                {
                    customerRowId: customerRowId,
                    customerId: customerId,
                    userCompanyId: customerCompanyId,
                    firstName: (firstName || "").trim(),
                    lastName: (lastName || "").trim(),
                    email: trimmedEmail,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            )
            .then((res) => {
                setSaving(false);
                const result = res.data || {};
                if (!result.success) {
                    // Everything the operator can act on - an unverifiable customer, a duplicate address,
                    // no default role configured - comes back as a message rather than an HTTP failure.
                    notify(result.message || MESSAGES.ERROR, "error", 5000);
                    return;
                }
                // The account exists either way, so the grid is refreshed either way - but an invitation
                // that did not go out is not a clean success, and the operator has to know to resend it.
                notify(
                    result.message || MESSAGES.CUSTOMER_ACCOUNT_CREATED,
                    result.invitationSent === false ? "warning" : "success",
                    result.invitationSent === false ? 8000 : 4000
                );
                reset();
                if (onCreated) onCreated();
            })
            .catch((err) => {
                setSaving(false);
                notify(err?.response?.data?.message || MESSAGES.ERROR, "error", 5000);
            });
    };

    if (!visible) return null;

    return (
        <Popup
            visible={visible}
            onHiding={handleClose}
            dragEnabled={true}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title="Create Customer Account"
            container=".dx-viewport"
            width="34vw"
            height="auto"
        >
            {/* Creating an account makes an identity-store write and sends an email, so the save is not
                instant - the panel keeps the operator from pressing Save twice. */}
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                visible={saving}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
            />
            <div className="formfield-mb-2">
                <SelectBox
                    dataSource={customers}
                    value={customerRowId}
                    valueExpr="id"
                    displayExpr={customerDisplay}
                    searchEnabled={true}
                    searchMode="contains"
                    searchExpr={["identityid", "customerName", "facilityName"]}
                    showClearButton={true}
                    placeholder="Select a customer"
                    onSelectionChanged={(e) => {
                        // The bare code off the selected row, not the box's display text - that carries
                        // the customer name too, and the claim must hold the code alone.
                        const selected = e.selectedItem;
                        setCustomerRowId(selected ? selected.id : null);
                        setCustomerId(selected ? (selected.identityid || "").trim() : "");
                        setCustomerCompanyId(selected ? (selected.userCompanyId || "").toString().trim() : "");
                    }}
                    stylingMode="outlined"
                    labelMode="static"
                    label="Customer"
                />
            </div>
            <div className="formfield-mb-2">
                <TextBox
                    value={firstName}
                    onValueChanged={(e) => setFirstName(e.value)}
                    stylingMode="outlined"
                    labelMode="static"
                    label="First Name"
                    maxLength={100}
                />
            </div>
            <div className="formfield-mb-2">
                <TextBox
                    value={lastName}
                    onValueChanged={(e) => setLastName(e.value)}
                    stylingMode="outlined"
                    labelMode="static"
                    label="Last Name"
                    maxLength={100}
                />
            </div>
            <div className="formfield-mb-2">
                <TextBox
                    value={email}
                    onValueChanged={(e) => setEmail(e.value)}
                    stylingMode="outlined"
                    labelMode="static"
                    label="Email Address"
                    maxLength={256}
                />
            </div>

            <PopupToolbarItem
                toolbar="bottom"
                location="after"
                widget="dxButton"
                options={{
                    text: "Save",
                    type: "default",
                    stylingMode: "contained",
                    disabled: saving,
                    onClick: handleSave,
                }}
            />
            <PopupToolbarItem
                toolbar="bottom"
                location="after"
                widget="dxButton"
                options={{
                    text: "Cancel",
                    onClick: handleClose,
                }}
            />
        </Popup>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(CreateCustomerAccount);
